import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Calculator, BookOpen, Waves, Landmark, Languages, Palette, Play, Timer, Coffee, Trophy, ArrowRight, CheckCircle2, ClipboardList, MessageCircle, X, Bell, Zap, LifeBuoy } from "lucide-react";
import StreakCalendar from "@/components/StreakCalendar";
import { AssignmentDueGroups, sortAssignmentsByDueDate } from "@/components/AssignmentDueGroups";
import { DashboardSkeleton, LoadError } from "@/components/PageState";

const ICONS = { Calculator, BookOpen, Waves, Landmark, Languages, Palette };

export default function StudentDashboard() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [progress, setProgress] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [worksheets, setWorksheets] = useState([]);
  const [heatmap, setHeatmap] = useState({ heatmap: {}, streak: 0 });
  const [nudges, setNudges] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [breakOn, setBreakOn] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [timerRun, setTimerRun] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  const load = useCallback(async () => {
    try {
      // Ping activity FIRST so today's streak reflects immediately
      await api.post("/activity/ping").catch(() => {});
      const [t, s, p, a, w, hm, n, pr, asg, msg] = await Promise.all([
        api.get("/today"),
        api.get("/curriculum/subjects"),
        api.get("/progress"),
        api.get("/achievements"),
        api.get("/worksheets"),
        api.get("/activity/heatmap"),
        api.get("/nudges"),
        api.get("/preferences"),
        api.get("/assignments"),
        api.get("/messages"),
      ]);
      setToday(t.data); setSubjects(s.data); setProgress(p.data); setAchievements(a.data);
      setWorksheets(w.data); setHeatmap(hm.data);
      setNudges(n.data.filter((x) => !x.read));
      setPrefs(pr.data || {});
      setAssignments(asg.data); setMessages(msg.data);
      setError("");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Today could not load.");
    }
  }, []);
  useEffect(() => { load(); }, [load, retry]);

  // Poll for new nudges every 15s (real-time-ish)
  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get("/nudges");
        setNudges(data.filter((x) => !x.read));
      } catch {}
    };
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
  }, []);

  const dismissNudge = async (id) => {
    try { await api.post(`/nudges/${id}/read`); setNudges((prev) => prev.filter((x) => x.id !== id)); } catch {}
  };

  useEffect(() => {
    if (!timerRun) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [timerRun]);

  const completedIds = new Set(progress.filter((p) => p.status === "completed").map((p) => p.lesson_id));
  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  const todayStr = new Date().toISOString().slice(0, 10);
  const reminders = sortAssignmentsByDueDate(assignments.filter((a) => a.due_date && !["graded", "submitted"].includes(a.status) && a.due_date <= todayStr));
  const urgentMsgs = messages.filter((m) => m.to_id === user?.id && m.priority === "urgent" && !m.read);

  if (error) return <LoadError message={error} onRetry={()=>{setError("");setRetry((value)=>value+1);}} testId="student-dashboard-load-error" />;
  if (!today) return <DashboardSkeleton testId="student-dashboard-loading" cards={4} />;

  return (
    <div className="space-y-8 rounded-3xl p-2 sm:p-4"
      style={prefs.background_image
        ? { backgroundImage: `linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url(${prefs.background_image})`, backgroundSize: "cover", backgroundPosition: "center" }
        : prefs.background_color ? { background: prefs.background_color } : {}}
      data-testid="student-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          {prefs.avatar && <img src={prefs.avatar} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-white shadow" data-testid="student-avatar" />}
          <div>
            <div className="chip mb-2">Today</div>
            <h1 data-testid="student-welcome-heading" className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Welcome to the Reef {user?.name?.split(" ")[0]}</h1>
            <p className="text-slate-600 mt-1">Small steps. Real progress. You've got this.</p>
            {prefs.music_url && (
              <a data-testid="student-music" href={prefs.music_url} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white/70 border border-slate-200 text-slate-700 font-semibold">🎧 Play focus music</a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase tracking-widest">Points</div>
            <div className="font-display text-2xl font-bold text-teal-700" data-testid="student-points">{user?.points ?? 0}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase tracking-widest">Badges</div>
            <div className="font-display text-2xl font-bold text-amber-600" data-testid="student-badge-count">{achievements.length}</div>
          </div>
        </div>
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="student-today-metrics">
        <ProgressMetric label="Curriculum lessons completed" value={`${today.metrics.curriculum_lessons_completed}/${today.metrics.curriculum_lessons_total}`} testId="today-curriculum-lessons" />
        <ProgressMetric label="Assignments turned in" value={today.metrics.assignments_turned_in} testId="today-assignments-turned-in" />
        <ProgressMetric label="Assignments graded" value={today.metrics.assignments_graded} testId="today-assignments-graded" />
        <ProgressMetric label="Assignment records" value={today.metrics.assignments_total} testId="today-assignment-count" />
      </section>

      {/* Assignment reminders */}
      {reminders.length > 0 && (
        <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200" data-testid="reminders-banner">
          <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm"><Bell size={16} /> Due today / overdue</div>
          <div className="mt-2 grid gap-1">
            {reminders.map((a) => (
              <Link key={a.id} to={`/assignments/${a.id}`} data-testid={`reminder-${a.id}`}
                className="flex items-center justify-between text-sm text-slate-800 hover:text-rose-700">
                <span>{a.title} <span className="text-slate-500">· {a.subject}</span></span>
                <span className="text-xs font-semibold">{a.due_date < todayStr ? "Overdue" : "Due today"}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Urgent messages */}
      {urgentMsgs.length > 0 && (
        <div className="space-y-2" data-testid="urgent-msgs-banner">
          {urgentMsgs.map((m) => (
            <Link key={m.id} to="/messages" data-testid={`urgent-msg-${m.id}`}
              className="flex items-start gap-3 p-4 rounded-2xl bg-sky-50 border border-sky-200 hover:border-sky-300">
              <Zap className="text-sky-600 shrink-0 mt-0.5" size={18} />
              <div>
                <div className="text-xs text-sky-800 uppercase tracking-widest font-semibold">Instant message from {m.from_name}</div>
                <div className="text-slate-800 mt-1">{m.subject ? <b>{m.subject}: </b> : null}{m.body}</div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Parent nudges banner */}
      {nudges.length > 0 && (
        <div className="space-y-2" data-testid="nudges-banner">
          {nudges.map((n) => (
            <div key={n.id} data-testid={`nudge-${n.id}`}
              className="flex items-start justify-between gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
              <div className="flex gap-3">
                <MessageCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
                <div>
                  <div className="text-xs text-amber-800 uppercase tracking-widest font-semibold">Note from your parent-teacher</div>
                  <div className="text-slate-800 mt-1">{n.message}</div>
                </div>
              </div>
              <button data-testid={`nudge-dismiss-${n.id}`} onClick={() => dismissNudge(n.id)} className="text-amber-700 hover:text-amber-900"><X size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Missions */}
      <div className="grid md:grid-cols-2 gap-6" data-testid="today-missions">
        {(today?.missions || []).map((m, i) => (
          <MissionCard key={i} mission={m} subjects={subjects} completedIds={completedIds} />
        ))}
        {today?.missions?.length === 0 && (
          <div className="p-8 rounded-3xl bg-white border border-slate-200 text-center col-span-2">
            <CheckCircle2 className="mx-auto text-teal-600" size={36} />
            <div className="mt-3 font-display text-lg font-semibold">You're all caught up!</div>
            <p className="text-slate-500 text-sm">Explore a subject below for extra practice.</p>
          </div>
        )}
      </div>

      {/* Need help? */}
      <button data-testid="dashboard-stuck-card" onClick={() => window.dispatchEvent(new Event("open-stuck"))}
        className="w-full text-left p-5 rounded-3xl bg-amber-50 border border-amber-200 hover:border-amber-300 transition-colors flex items-center gap-4">
        <span className="grid place-items-center w-12 h-12 rounded-2xl bg-amber-500 text-white shrink-0"><LifeBuoy size={22} /></span>
        <div>
          <div className="font-display text-lg font-semibold text-slate-900">HELP! I'm stuck</div>
          <div className="text-sm text-slate-600">Get a hint that guides you — never just the answer. Your parent-teacher will know you asked.</div>
        </div>
      </button>

      {/* Tools row */}
      <div className="grid sm:grid-cols-4 gap-4">
        <div className="p-5 rounded-2xl bg-white border border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-widest">Focus timer</div>
            <div className="font-display text-2xl font-bold text-slate-900" data-testid="focus-timer">{fmt(seconds)}</div>
          </div>
          <button data-testid="focus-timer-toggle" onClick={() => setTimerRun((r) => !r)}
            className="px-4 py-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold flex items-center gap-2">
            <Timer size={14} /> {timerRun ? "Pause" : "Start"}
          </button>
        </div>
        <button data-testid="break-btn" onClick={() => setBreakOn((b) => !b)}
          className={`p-5 rounded-2xl border text-left ${breakOn ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-widest"><Coffee size={12} /> Movement break</div>
          <div className="font-display text-lg font-semibold text-slate-900 mt-1">
            {breakOn ? "Stand up, stretch, sip water — 90 seconds." : "Take a break"}
          </div>
        </button>
        <Link to="/achievements" data-testid="badges-cta" className="p-5 rounded-2xl bg-white border border-slate-200 flex items-center justify-between hover:border-teal-300 transition-colors">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-widest flex items-center gap-1"><Trophy size={12} /> Achievements</div>
            <div className="font-display text-lg font-semibold text-slate-900 mt-1">See your badges</div>
          </div>
          <ArrowRight size={18} className="text-slate-400" />
        </Link>
        <StreakCalendar heatmap={heatmap.heatmap} streak={heatmap.streak} />
      </div>

      {/* Worksheets */}
      {worksheets.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2"><ClipboardList size={18} /> Worksheets</h2>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="worksheets-list">
            {worksheets.slice(0, 6).map((w) => (
              <Link key={w.id} to={`/worksheets/${w.id}`} data-testid={`worksheet-card-${w.id}`}
                className="p-4 rounded-2xl bg-white border border-slate-200 hover:border-teal-300 transition-colors flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{w.title}</div>
                  <div className="text-xs text-slate-500">{w.subject} · {w.items.length} items · {w.max_score} pts</div>
                </div>
                <span className="chip">{w.status === "graded" ? `${w.score}/${w.max_score}` : "Do it"}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <AssignmentDueGroups
        assignments={today?.upcoming || []}
        linkCards
        testIdPrefix="student-open-assignments"
        totalCount={today?.open_assignments?.length}
      />

      {/* Subjects */}
      <section>
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Explore subjects</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="subject-grid">
          {subjects.map((s) => {
            const Icon = ICONS[s.icon] || BookOpen;
            const summary = today.by_subject?.[s.slug] || { lessons_total: s.lesson_count || 0, lessons_done: 0 };
            const total = summary.lessons_total;
            const done = summary.lessons_done;
            return (
              <Link key={s.slug} to={`/subjects/${s.slug}`} data-testid={`subject-card-${s.slug}`}
                className="group p-6 rounded-3xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all">
                <span className={`inline-grid place-items-center w-11 h-11 rounded-2xl bg-teal-50 text-teal-800`}><Icon size={20} strokeWidth={2.5} /></span>
                <div className="mt-4 font-display text-lg font-semibold text-slate-900">{s.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.tagline}</div>
                <div className="mt-4 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-teal-600" style={{ width: `${Math.min(100, (done/total)*100)}%` }} />
                </div>
                <div className="mt-2 text-xs text-slate-500">{done}/{total} lessons completed</div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function ProgressMetric({ label, value, testId }) {
  return <div className="p-4 rounded-2xl bg-white/90 border border-slate-200"><div className="text-xs uppercase tracking-widest text-slate-500">{label}</div><div data-testid={testId} className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div></div>;
}

function MissionCard({ mission, subjects, completedIds }) {
  if (mission.type === "lesson") {
    const l = mission.lesson;
    const sub = subjects.find((s) => s.slug === l.subject);
    return (
      <Link to={`/lessons/${l.id}`} data-testid={`mission-lesson-${l.id}`}
        className="group p-6 rounded-3xl bg-white border border-slate-200 hover:border-teal-400 hover:shadow-md transition-all flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="chip">{sub?.title || l.subject}</span>
          <span className="text-xs text-slate-500">~{l.minutes} min</span>
        </div>
        <div className="font-display text-2xl font-bold text-slate-900 leading-tight">{l.title}</div>
        <p className="text-sm text-slate-600 line-clamp-2">{l.summary}</p>
        <div className="mt-auto inline-flex items-center gap-2 text-teal-700 font-semibold text-sm">
          <Play size={14} /> Start mission <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
        </div>
      </Link>
    );
  }
  const a = mission.assignment;
  return (
    <Link to={`/assignments/${a.id}`} className="p-6 rounded-3xl bg-amber-50 border border-amber-200 hover:border-amber-300 flex flex-col gap-3" data-testid={`mission-assignment-${a.id}`}>
      <div className="flex items-center justify-between">
        <span className="chip" style={{ background: "hsl(38 92% 92%)", color: "hsl(38 92% 25%)", borderColor: "hsl(38 60% 82%)" }}>Assignment</span>
        <span className="text-xs text-amber-800">{a.due_date || "no due date"}</span>
      </div>
      <div className="font-display text-2xl font-bold text-slate-900 leading-tight">{a.title}</div>
      <p className="text-sm text-slate-700 line-clamp-2">{a.description}</p>
      <div className="mt-auto inline-flex items-center gap-2 text-amber-800 font-semibold text-sm">
        Open assignment <ArrowRight size={14} />
      </div>
    </Link>
  );
}
